import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Pencil,
  UserPlus,
  Link2,
  Users,
  MapPin,
  Eye,
  Layers,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Trash2,
  UserCog,
} from 'lucide-react';
import { API } from 'aws-amplify';
import * as mutations from '../../../graphql/mutations';
import fetchTeam from '../../../application/routes/teams/fetchTeam';
import fetchTeamMembers from '../../../application/routes/teams/fetchTeamMembers';
import featureFlag from '../../../application/common/featureFlag';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import { Button } from '../../components/ui/button';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';

export function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { hasTeamPermission } = usePermissions();

  const showEdit = hasTeamPermission(PermissionKeys.TeamsPeople_UpdateTeam, [
    teamId || '',
  ]);
  const showAddPerson = hasTeamPermission(
    PermissionKeys.TeamsPeople_AddPersonToTeam,
    [teamId || '']
  );

  const [myTeam, setMyTeam] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMemberLoading, _setIsMemberLoading] = useState(false);
  const [showTree, setShowTree] = useState(false);
  const [checkedMembers, setCheckedMembers] = useState<any[]>([]);

  const routeBackToProfile = location?.state?.profileAboutPage;
  const routeBackToGames = location?.state?.games;
  const userId = location?.state?.userId;

  useEffect(() => {
    if (!teamId) {
      return;
    }
    loadTeam();
  }, [teamId]);

  const loadTeam = async () => {
    setIsLoading(true);
    try {
      const team = await fetchTeam(teamId);
      if (!team) {
        setIsLoading(false);
        return;
      }
      setMyTeam(team);

      const teamMembers = await fetchTeamMembers(teamId);
      const activeMembers = (teamMembers || []).filter(
        (m: any) => m.status === 'active'
      );
      setMembers(activeMembers);
    } catch (err) {
      console.error('Error loading team:', err);
      toast.error('Failed to load team details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (routeBackToProfile) {
      navigate(`/v2/people/${userId}`);
    } else if (routeBackToGames) {
      navigate('/performance/games');
    } else {
      navigate('/v2/teams');
    }
  };

  const handleRemoveMembers = async (membersToRemove: any[]) => {
    if (!membersToRemove.length) {
      return;
    }
    if (membersToRemove.length === members.length) {
      toast.error('You must have at least one member on a team');
      return;
    }

    try {
      for (const member of membersToRemove) {
        await API.graphql({
          query: (mutations as any).modifyCircleMember,
          variables: {
            circleID: teamId,
            personID: member.id,
            status: 'removed',
            role: '',
          },
        });
      }

      setMembers((prev) =>
        prev.filter((m) => !membersToRemove.find((rm) => rm.id === m.id))
      );
      setCheckedMembers([]);

      toast.success(
        membersToRemove.length === 1
          ? `${membersToRemove[0].name} removed successfully`
          : `${membersToRemove.length} members removed successfully`
      );
    } catch (err) {
      console.error('Remove member error:', err);
      toast.error('Failed to remove member. Please try again.');
    }
  };

  const handleMemberCheck = useCallback((member: any, checked: boolean) => {
    if (checked) {
      setCheckedMembers((prev) => [...prev, member]);
    } else {
      setCheckedMembers((prev) => prev.filter((m) => m.id !== member.id));
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!myTeam) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Users className="size-12 mb-3" />
        <p className="text-lg font-medium">Team not found</p>
        <Button variant="outline" className="mt-4" onClick={handleBack}>
          Go back
        </Button>
      </div>
    );
  }

  if (myTeam.isDeleted) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Users className="size-12 mb-3" />
        <p className="text-lg font-medium">This team has been deleted</p>
        <Button variant="outline" className="mt-4" onClick={handleBack}>
          Go back
        </Button>
      </div>
    );
  }

  const childTeams = (myTeam?.children?.items || []).filter(
    (t: any) => !t.isDeleted
  );

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Hero Banner */}
      <div className="relative bg-muted/50 dark:bg-muted/30 px-4 md:px-12 pt-6 pb-16 md:pb-20">
        {/* Back button */}
        <Button
          variant="ghost"
          className="text-foreground hover:bg-black/5 dark:hover:bg-white/10 mb-4"
          onClick={handleBack}
        >
          <ArrowLeft className="size-4 mr-2" />
        </Button>

        {/* Team info centered */}
        <div className="flex flex-col items-center text-center">
          {myTeam.avatarFile ? (
            <img
              src={myTeam.avatarFile}
              alt={myTeam.name}
              className="size-28 md:size-36 rounded-full object-cover border-4 border-background shadow-md"
            />
          ) : (
            <div className="size-28 md:size-36 rounded-full bg-primary/15 dark:bg-primary/20 flex items-center justify-center border-4 border-background shadow-md">
              <span className="text-4xl md:text-5xl font-bold text-primary">
                {myTeam.name?.[0]?.toUpperCase()}
              </span>
            </div>
          )}
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mt-4">
            {myTeam.name} Team
          </h1>
          {myTeam.description && (
            <p className="text-sm md:text-base text-muted-foreground mt-1 max-w-md">
              {myTeam.description}
            </p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-center gap-6 md:gap-8 -mt-5 relative z-10">
        {showEdit && (
          <button
            onClick={() => navigate(`/v2/teams/edit/${teamId}`)}
            className="flex flex-col items-center gap-1.5 group"
          >
            <div className="size-11 rounded-full bg-card border border-border shadow-sm flex items-center justify-center group-hover:bg-muted transition-colors text-foreground">
              <Pencil className="size-4" />
            </div>
            <span className="text-xs font-medium text-primary">Edit</span>
          </button>
        )}
        {showAddPerson && (
          <button
            onClick={() => {
              toast.info('Use the edit page to add members');
              navigate(`/v2/teams/edit/${teamId}`);
            }}
            className="flex flex-col items-center gap-1.5 group"
          >
            <div className="size-11 rounded-full bg-card border border-border shadow-sm flex items-center justify-center group-hover:bg-muted transition-colors text-foreground">
              <UserPlus className="size-4" />
            </div>
            <span className="text-xs font-medium text-primary">
              Add members
            </span>
          </button>
        )}
        {showEdit && (
          <button
            onClick={() => {
              toast.info('Use the edit page to link teams');
              navigate(`/v2/teams/edit/${teamId}`);
            }}
            className="flex flex-col items-center gap-1.5 group"
          >
            <div className="size-11 rounded-full bg-card border border-border shadow-sm flex items-center justify-center group-hover:bg-muted transition-colors text-foreground">
              <Link2 className="size-4" />
            </div>
            <span className="text-xs font-medium text-primary">Link team</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex-1 px-4 md:px-12 py-6">
        <Tabs defaultValue="about" className="max-w-3xl mx-auto">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="members">
              Members ({members.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="about" className="mt-6">
            <AboutSection
              team={myTeam}
              childTeams={childTeams}
              showTree={showTree}
              setShowTree={setShowTree}
              onTeamClick={(id: string) => navigate(`/v2/teams/${id}`)}
            />
          </TabsContent>

          <TabsContent value="members" className="mt-6">
            <MembersSection
              members={members}
              checkedMembers={checkedMembers}
              onMemberCheck={handleMemberCheck}
              onRemoveMembers={handleRemoveMembers}
              isMemberLoading={isMemberLoading}
              teamId={teamId}
              onMembersUpdated={loadTeam}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ── About Section ───────────────────────────────────────────

function AboutSection({
  team,
  childTeams,
  showTree,
  setShowTree,
  onTeamClick,
}: {
  team: any;
  childTeams: any[];
  showTree: boolean;
  setShowTree: (v: boolean) => void;
  onTeamClick: (id: string) => void;
}) {
  const infoItems = [
    {
      icon: <MapPin className="size-4" />,
      label: 'Location',
      value: team.location || 'N/A',
    },
    {
      icon: <Eye className="size-4" />,
      label: 'Visibility',
      value: `Public - Approver: ${team.approverType || 'auto'}`,
    },
    {
      icon: <Layers className="size-4" />,
      label: 'Parent Team',
      value: team.parent?.name || 'None',
    },
    {
      icon: <Layers className="size-4" />,
      label: 'Level',
      value: team.level?.name || 'None',
    },
  ];

  return (
    <div className="space-y-4">
      {infoItems.map((item, i) => (
        <div
          key={i}
          className="flex items-center justify-between py-3 border-b border-border last:border-0"
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {item.icon}
            <span>{item.label}</span>
          </div>
          <span className="text-sm font-medium text-foreground">
            {item.value}
          </span>
        </div>
      ))}

      {/* Linked Teams */}
      <div className="flex items-center justify-between py-3 border-b border-border">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link2 className="size-4" />
          <span>Linked Teams</span>
        </div>
        <div className="text-right">
          {childTeams.length > 0 ? (
            <div className="space-y-1">
              {childTeams.map((ct: any) => (
                <p key={ct.id} className="text-sm font-medium text-foreground">
                  {ct.name}
                </p>
              ))}
              <button
                onClick={() => setShowTree(!showTree)}
                className="text-xs font-medium text-primary hover:underline flex items-center gap-1 ml-auto"
              >
                {showTree ? 'Hide tree' : 'Show tree'}
                {showTree ? (
                  <ChevronUp className="size-3" />
                ) : (
                  <ChevronDown className="size-3" />
                )}
              </button>
            </div>
          ) : (
            <span className="text-sm font-medium text-foreground">None</span>
          )}
        </div>
      </div>

      {/* Tree view */}
      {showTree && childTeams.length > 0 && (
        <div className="pl-6 border-l-2 border-border ml-4 space-y-3 pt-2">
          <button
            onClick={() => onTeamClick(team.id)}
            className="flex items-center gap-2 hover:bg-muted/50 rounded-lg p-2 transition-colors"
          >
            {team.avatarFile ? (
              <img
                src={team.avatarFile}
                alt={team.name}
                className="size-8 rounded-lg object-cover"
              />
            ) : (
              <div className="size-8 rounded-lg bg-muted flex items-center justify-center">
                <span className="text-xs font-semibold">
                  {team.name?.[0]?.toUpperCase()}
                </span>
              </div>
            )}
            <span className="text-sm font-medium text-foreground">
              {team.name}
            </span>
          </button>
          {childTeams.map((ct: any) => (
            <button
              key={ct.id}
              onClick={() => onTeamClick(ct.id)}
              className="flex items-center gap-2 hover:bg-muted/50 rounded-lg p-2 transition-colors ml-4"
            >
              {ct.avatarFile ? (
                <img
                  src={ct.avatarFile}
                  alt={ct.name}
                  className="size-8 rounded-lg object-cover"
                />
              ) : (
                <div className="size-8 rounded-lg bg-muted flex items-center justify-center">
                  <span className="text-xs font-semibold">
                    {ct.name?.[0]?.toUpperCase()}
                  </span>
                </div>
              )}
              <span className="text-sm font-medium text-foreground">
                {ct.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Members Section ─────────────────────────────────────────

function MembersSection({
  members,
  checkedMembers,
  onMemberCheck,
  onRemoveMembers,
  isMemberLoading,
  teamId,
  onMembersUpdated,
}: {
  members: any[];
  checkedMembers: any[];
  onMemberCheck: (member: any, checked: boolean) => void;
  onRemoveMembers: (members: any[]) => void;
  isMemberLoading: boolean;
  teamId?: string;
  onMembersUpdated: () => void;
}) {
  const { checkPermission, hasTeamPermission } = usePermissions();
  const canChangeRole = checkPermission(
    PermissionKeys.TeamsPeople_ChangePersonsRole
  );
  const canRemove = hasTeamPermission(
    PermissionKeys.TeamsPeople_RemoveSomeoneFromTeam,
    [teamId || '']
  );

  const [roleModalMember, setRoleModalMember] = useState<any>(null);
  const [confirmRemoveMembers, setConfirmRemoveMembers] = useState<
    any[] | null
  >(null);

  const handleConfirmRemove = () => {
    if (confirmRemoveMembers) {
      onRemoveMembers(confirmRemoveMembers);
      setConfirmRemoveMembers(null);
    }
  };

  if (isMemberLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Bulk remove bar */}
      {checkedMembers.length > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <span className="text-sm font-medium text-destructive">
            {checkedMembers.length} selected
          </span>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirmRemoveMembers(checkedMembers)}
          >
            <Trash2 className="size-3.5 mr-1.5" />
            Remove
          </Button>
        </div>
      )}

      {members.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
          <Users className="size-10 mb-2" />
          <p className="text-sm">No members yet</p>
        </div>
      ) : (
        members.map((member) => {
          const showMenu = canChangeRole || canRemove;
          return (
            <div
              key={member.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted/30 transition-colors"
            >
              {canRemove && (
                <input
                  type="checkbox"
                  className="size-4 rounded border-border accent-primary shrink-0"
                  checked={checkedMembers.some((m) => m.id === member.id)}
                  onChange={(e) => onMemberCheck(member, e.target.checked)}
                />
              )}
              {member.avatarFile ? (
                <img
                  src={member.avatarFile}
                  alt={member.name}
                  className="size-10 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-semibold text-primary">
                    {member.initials || member.name?.[0]?.toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {member.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {member.title || member.functionalRoles || 'Member'}
                </p>
              </div>
              {/* Team role badge */}
              {member.circleMember?.memberRole && (
                <TeamRoleBadge role={member.circleMember.memberRole} />
              )}
              <span className="text-xs text-muted-foreground hidden sm:block">
                {member.circleMember?.memberType
                  ? member.circleMember.memberType.charAt(0).toUpperCase() +
                    member.circleMember.memberType.slice(1)
                  : 'Member'}
              </span>
              {showMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-8">
                      <MoreHorizontal className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canChangeRole && (
                      <DropdownMenuItem
                        onClick={() => setRoleModalMember(member)}
                      >
                        <UserCog className="size-3.5 mr-2" />
                        Change Role
                      </DropdownMenuItem>
                    )}
                    {canRemove && (
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setConfirmRemoveMembers([member])}
                      >
                        <Trash2 className="size-3.5 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })
      )}

      {/* Remove Confirmation Modal */}
      {confirmRemoveMembers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setConfirmRemoveMembers(null)}
          />
          <div className="relative bg-background rounded-xl border border-border shadow-xl w-full max-w-sm mx-4 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-foreground text-center">
              {confirmRemoveMembers.length === 1
                ? `Remove ${confirmRemoveMembers[0].name}?`
                : `Remove ${confirmRemoveMembers.length} members?`}
            </h2>
            <p className="text-sm text-muted-foreground text-center">
              You are able to add members back after removal.
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setConfirmRemoveMembers(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleConfirmRemove}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {roleModalMember && (
        <ChangeRoleModal
          member={roleModalMember}
          onClose={() => setRoleModalMember(null)}
          onSaved={() => {
            setRoleModalMember(null);
            onMembersUpdated();
          }}
        />
      )}
    </div>
  );
}

// ── Team Role Badge ─────────────────────────────────────────

function TeamRoleBadge({ role }: { role: string }) {
  const roleStyles: Record<string, string> = {
    closer: 'bg-orange-100 text-orange-700',
    setter: 'bg-sky-100 text-sky-700',
    manager: 'bg-emerald-100 text-emerald-700',
    scheduler: 'bg-gray-100 text-gray-700',
    other: 'bg-primary text-primary-foreground',
  };

  const style = roleStyles[role] || 'bg-muted text-muted-foreground';

  return (
    <span
      className={cn(
        'hidden sm:inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold',
        style
      )}
    >
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );
}

// ── Change Role Modal ───────────────────────────────────────

function ChangeRoleModal({
  member,
  onClose,
  onSaved,
}: {
  member: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const hideSetterCloser = featureFlag('noSetterCloser');

  const roleOptions = [
    { label: 'Member', value: 'member' },
    { label: 'Moderator', value: 'moderator' },
    { label: 'Admin', value: 'admin' },
  ];

  const teamRoleOptions = [
    { label: 'None', value: 'none' },
    ...(hideSetterCloser ? [] : [{ label: 'Setter', value: 'setter' }]),
    ...(hideSetterCloser ? [] : [{ label: 'Closer', value: 'closer' }]),
    { label: 'Manager', value: 'manager' },
    { label: 'Scheduler', value: 'scheduler' },
    { label: 'Other', value: 'other' },
  ];

  const [role, setRole] = useState(
    member.orgs?.items?.[0]?.memberType || 'member'
  );
  const [teamRole, setTeamRole] = useState(
    member.circleMember?.memberRole || 'none'
  );
  const [isSaving, setIsSaving] = useState(false);

  const originalRole = member.orgs?.items?.[0]?.memberType || 'member';
  const originalTeamRole = member.circleMember?.memberRole || 'none';

  const hasChanges = role !== originalRole || teamRole !== originalTeamRole;

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      // Update org role if changed
      if (role !== originalRole) {
        await API.graphql({
          query: (mutations as any).updateOrgMember,
          variables: {
            input: {
              id: member.orgs.items[0].id,
              memberType: role,
            },
          },
        });
      }

      // Update team role if changed
      if (teamRole !== originalTeamRole) {
        await API.graphql({
          query: (mutations as any).updateCircleMember,
          variables: {
            input: {
              id: member.circleMember.id,
              memberRole: teamRole === 'none' ? '' : teamRole,
            },
          },
        });
      }

      toast.success(`${member.name}'s role updated successfully`);
      onSaved();
    } catch (err) {
      console.error('Change role error:', err);
      toast.error('Failed to update role. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-xl border border-border shadow-xl w-full max-w-md mx-4 p-6 space-y-5">
        <h2 className="text-lg font-semibold text-foreground text-center">
          Change Role
        </h2>
        <p className="text-sm text-muted-foreground text-center">
          {member.name}
        </p>

        {/* Role (org-level) */}
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-foreground pl-1">
            Role
          </label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Team Role */}
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-foreground pl-1">
            Team Role
          </label>
          <Select value={teamRole} onValueChange={setTeamRole}>
            <SelectTrigger>
              <SelectValue placeholder="Select team role" />
            </SelectTrigger>
            <SelectContent>
              {teamRoleOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Warning for admin changes */}
        {role === 'admin' && originalRole !== 'admin' && (
          <p className="text-xs text-destructive">
            {member.name} will have admin access to all your accounts.
          </p>
        )}
        {role !== 'admin' && originalRole === 'admin' && (
          <p className="text-xs text-destructive">
            {member.name} will lose all admin permissions.
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
